import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  GetScheduleFormsRequestDTO,
  GetScheduleFormsResponseDTO,
  MODULE,
  PdfScope,
  ScheduleFormOrderByFieldEnumSchema,
  ScheduleFormSubmissionColumnId,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import LinkViewIcon from '@/components/icons/link-view';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { buildFilterFields } from '@/queries/column-filter';
import { getScheduleFormsInfiniteQueryOptions, scheduleFormKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { exportRecordsToPdfZip } from '@/utils/bulk-pdf-export';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { useDropTableColumn } from '@/utils/table-meta';

import { TopbarActions } from '../topbar-actions';
import {
  NoScheduleFormSubmissionAvailableDisplay,
  NoScheduleFormSubmissionFoundFromFiltersDisplay,
  NoScheduleFormSubmissionFoundFromSearchDisplay,
  ScheduleFormSubmissionsTable,
} from './table';
import { FILTER_FIELD_MAP, groupOptions, columns as scheduleFormColumns } from './table-meta';

const ScheduleActivityPage = () => {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const org = useUserStore((state) => state.org);
  const { canEditScheduleRecord, canExportCsv } = usePermissions();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.ScheduleFormSubmissions, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: scheduleFormColumns(),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  useDropTableColumn({
    module: MODULE.ScheduleFormSubmissions,
    columnIds: ['contact'],
  });

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: scheduleFormsData,
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isFetchingNextPage: isFetchingNextScheduleFormsPage,
    error: scheduleFormsError,
  } = useInfiniteQuery(
    getScheduleFormsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (columnId, raw) => (columnId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as z.infer<typeof ScheduleFormOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedScheduleForms = useMemo(() => {
    return (incomingData: typeof scheduleFormsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteScheduleForms, isPending: isDeletingScheduleForms } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteScheduleForms({ scheduleFormIds: ids }),
    onSuccess: () => {
      toast.success('Schedule forms deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteScheduleForms = () => {
    if (scheduleFormsData?.pages) {
      const toDelete = getSelectedScheduleForms(scheduleFormsData);
      deleteScheduleForms(toDelete.map((scheduleForm) => scheduleForm.id));
    }
  };

  const selectedScheduleForms = useMemo(() => getSelectedScheduleForms(scheduleFormsData), [scheduleFormsData, getSelectedScheduleForms]);
  // Members see Delete when every selected schedule form record is theirs; Admins always see it.
  const canBulkActOnSelectedScheduleForms =
    selectedScheduleForms.length > 0 && selectedScheduleForms.every((scheduleForm) => canEditScheduleRecord(scheduleForm.submittedById));

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildScheduleFormsExportRequest(): RegisterExportQuery<'schedule-forms'> {
    const selected = getSelectedScheduleForms(scheduleFormsData);
    return {
      queryType: 'schedule-forms',
      filters: { fields: { id: selected.map((scheduleForm) => scheduleForm.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as z.infer<typeof ScheduleFormOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!scheduleFormsData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildScheduleFormsExportRequest(), filename: buildCsvExportFilename('schedule-forms') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!scheduleFormsData?.pages) return;
    const records = getSelectedScheduleForms(scheduleFormsData);
    if ((scope === 'records' || scope === 'register-and-records') && records.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(
      buildPdfExportJob({
        scope,
        recordType: 'schedule-form',
        records,
        register: buildScheduleFormsExportRequest(),
      }),
    );
  }

  async function handlePdfExportZip() {
    if (!scheduleFormsData?.pages) return;
    setIsDownloadingPdfs(true);
    try {
      await exportRecordsToPdfZip(getSelectedScheduleForms(scheduleFormsData), 'schedule-forms');
      toast.success('PDF export completed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export PDFs');
    } finally {
      setIsDownloadingPdfs(false);
    }
  }

  useEffect(() => {
    if (scheduleFormsError) {
      toast.error(scheduleFormsError.message);
    }
  }, [toast, scheduleFormsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isError: !!scheduleFormsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => scheduleFormsData?.pages.flatMap((page) => page.data) ?? [], [scheduleFormsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedScheduleForms.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Activity</TablePageTitle>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.ScheduleFormSubmissions, [])}
                    />
                  )}
                </div>

                <div className="flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) => updatePageConfig({ table: { grouping: value ? [value] : [] } })}
                    />
                    <ColumnsDialog<GetScheduleFormsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={scheduleFormColumns()}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: ScheduleFormSubmissionColumnId; active: boolean }> },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Mechanic App"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedScheduleForms.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedScheduleForms.length} totalCount={scheduleFormsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} onClick={handleCsvExport} label="Download CSV" />}
                {isRegisterPdfExportEnabled ? (
                  <DownloadPdfMenu recordNoun="schedule" onSelect={handlePdfScopeSelect} canExportRegister={canExportCsv} />
                ) : (
                  <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExportZip} disabled={isDownloadingPdfs} />
                )}
                {canBulkActOnSelectedScheduleForms && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    onClick={() => setOpenDeleteConfirmation(true)}
                    label="Delete"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <ScheduleFormSubmissionsTable
          data={flattenedData}
          loading={(isFetchingScheduleForms && !isFetchingNextScheduleFormsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.ScheduleFormSubmissions, [])} />
            ) : (
              <NoScheduleFormSubmissionAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.ScheduleFormSubmissions, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextScheduleFormsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextScheduleFormsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteScheduleForms}
        loading={isDeletingScheduleForms}
        title="Delete schedule forms?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these schedule forms?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
};

export default ScheduleActivityPage;
